import { env } from '@/lib/env';
import { ApiError, parseApiError } from '@/lib/api-error';
import { useSessionStore } from '@/stores/session';
import {
  EFFETA_ROLE_UNKNOWN,
  IDENTITY_PROVIDER_UNAVAILABLE,
  classifySessionFailure,
} from '@/lib/auth-refresh';

// Tipos públicos ------------------------------------------------------------

// Modo de parseo de la respuesta:
// - 'json' (default): intenta parsear el body como JSON.
// - 'none': para 204 No Content u otros casos en los que el caller no necesita body.
// - 'blob': para descargas de archivos (xlsx, pdf). El caller recibe
//   `{ blob, filename }` donde `filename` viene de `Content-Disposition` si
//   está presente, o `null` para que el caller aplique su default.
export type ParseResponseMode = 'json' | 'none' | 'blob';

// Payload devuelto cuando `parseResponse: 'blob'`. El filename es opcional
// porque no siempre viene en el header — el caller decide el fallback.
export interface HttpBlobResponse {
  blob: Blob;
  filename: string | null;
}

// Extrae el filename del header `Content-Disposition`. Soporta:
//   - `attachment; filename="foo.xlsx"` (comilladas)
//   - `attachment; filename=foo.xlsx` (sin comillas)
//   - `attachment; filename*=UTF-8''foo%20bar.xlsx` (RFC 5987)
// Devuelve null si el header está ausente o no matchea.
export function parseContentDispositionFilename(
  header: string | null,
): string | null {
  if (!header) return null;
  // Preferimos `filename*=` (RFC 5987) porque codifica UTF-8 correctamente.
  const rfc5987 = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (rfc5987 && rfc5987[1]) {
    try {
      return decodeURIComponent(rfc5987[1].trim());
    } catch {
      // decodeURIComponent puede fallar si el header viene corrupto; caemos
      // al fallback plano.
    }
  }
  const plain = /filename=("([^"]+)"|([^;]+))/i.exec(header);
  if (plain) {
    return (plain[2] ?? plain[3] ?? '').trim() || null;
  }
  return null;
}

export interface HttpOptions extends Omit<RequestInit, 'body'> {
  // Body sin serializar. Si es FormData se envía tal cual; si es objeto se hace
  // JSON.stringify automático. Strings o ArrayBuffer pasan crudos.
  body?: unknown;
  // AbortSignal explícito para cancelar la request (TanStack Query lo provee).
  signal?: AbortSignal;
  // Cómo procesar la respuesta exitosa.
  parseResponse?: ParseResponseMode;
  // Si true, no se adjunta el header Authorization. Útil para /auth/login.
  skipAuth?: boolean;
  // Si true, un 401 (o 403 `EFFETA_ROLE_UNKNOWN`) NO dispara renovación +
  // retry — se propaga directo. Lo usa el retry interno post-renovación para
  // cortar loops.
  skipRefreshOn401?: boolean;
}

// Construcción de URL -------------------------------------------------------

// Concatena base + path evitando dobles slashes.
// `path` puede ser absoluto ("/users") o relativo ("users").
function buildUrl(path: string): string {
  // URL absoluta: respetar tal cual (ej: assets externos).
  if (/^https?:\/\//i.test(path)) return path;

  const base = env.VITE_API_BASE_URL.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

// Body + headers ------------------------------------------------------------

// Resultado de la preparación del body: qué mandar a fetch + headers a mergear.
interface PreparedBody {
  body: BodyInit | null;
  contentType: string | null;
}

// Determina cómo serializar el body y si setear Content-Type.
function prepareBody(body: unknown): PreparedBody {
  if (body === undefined || body === null) {
    return { body: null, contentType: null };
  }

  // FormData: el browser setea Content-Type con el boundary correcto.
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return { body, contentType: null };
  }

  // Tipos crudos que fetch acepta sin serializar.
  if (
    typeof body === 'string' ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams)
  ) {
    // Para URLSearchParams el browser setea automáticamente, pero lo dejamos
    // explícito en string y Blob para no asumir.
    return { body: body as BodyInit, contentType: null };
  }

  // Default: objeto plano → JSON.
  return {
    body: JSON.stringify(body),
    contentType: 'application/json',
  };
}

// Reintentos ante Effetá caído ---------------------------------------------

// 503 `IDENTITY_PROVIDER_UNAVAILABLE` en un pedido común: el back no pudo
// conseguir las claves de Effetá para validar el pase, así que el pedido no
// se procesó y repetirlo es seguro. Se reintenta sin cerrar la sesión.
export const PROVIDER_RETRY_DELAYS_MS = [2_000, 4_000] as const;

function wait(ms: number, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('aborted', 'AbortError'));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

// El pase no sirve y conviene renovarlo una vez antes de rendirse:
// - 401: ausente, vencido o inválido (también si el timer de renovación no
//   corrió porque la pestaña estuvo dormida).
// - 403 `EFFETA_ROLE_UNKNOWN`: pase vivo con un rol que ya cambió en Effetá.
function shouldRenewPass(error: ApiError): boolean {
  if (error.statusCode === 401) return true;
  return error.statusCode === 403 && error.errorCode === EFFETA_ROLE_UNKNOWN;
}

// Función principal ---------------------------------------------------------

export async function http<T = unknown>(
  path: string,
  options: HttpOptions = {},
): Promise<T> {
  return send<T>(path, options, 0);
}

async function send<T>(path: string, options: HttpOptions, providerRetries: number): Promise<T> {
  const {
    body,
    signal,
    parseResponse = 'json',
    skipAuth = false,
    skipRefreshOn401 = false,
    headers,
    ...init
  } = options;

  const url = buildUrl(path);
  const prepared = prepareBody(body);

  // Mergeamos headers en orden: defaults < contentType < auth < user-supplied.
  // Usamos plain object para que el caller pueda sobreescribir cualquier default.
  const mergedHeaders: Record<string, string> = {
    Accept: 'application/json',
  };

  if (prepared.contentType) {
    mergedHeaders['Content-Type'] = prepared.contentType;
  }

  if (!skipAuth) {
    const token = useSessionStore.getState().token;
    if (token) {
      mergedHeaders.Authorization = `Bearer ${token}`;
    }
  }

  // Aplicamos headers del caller al final para permitir override explícito.
  if (headers) {
    const userHeaders = new Headers(headers);
    userHeaders.forEach((value, key) => {
      mergedHeaders[key] = value;
    });
  }

  // Por default, los endpoints de `/auth/*` mandan la cookie httpOnly (la
  // necesita el back para validar refresh/logout). El resto usa el default
  // del browser ('same-origin'), salvo que el caller fuerce un valor.
  const credentials: RequestCredentials =
    init.credentials ?? (path.startsWith('/auth/') ? 'include' : 'same-origin');

  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      credentials,
      headers: mergedHeaders,
      body: prepared.body,
      signal,
    });
  } catch (cause) {
    // Network error / abort / DNS / CORS → no llegó a haber Response.
    // Re-lanzamos como ApiError genérico para uniformar el tipo en hooks.
    if (cause instanceof DOMException && cause.name === 'AbortError') {
      throw cause;
    }
    throw new ApiError({
      statusCode: 0,
      message: cause instanceof Error ? cause.message : 'Network error',
    });
  }

  if (!response.ok) {
    // Intentamos leer el body para extraer el envelope NestJS.
    // Si falla el parseo, parseApiError cae al fallback con response.statusText.
    let errorBody: unknown = null;
    try {
      errorBody = await response.clone().json();
    } catch {
      // Body no-JSON o vacío: dejamos errorBody = null.
    }
    const apiError = parseApiError(response, errorBody);

    // Pase vencido o con rol viejo: renovación single-flight (la del session
    // store) + reintento UNA vez. Saltamos la renovación cuando:
    //  - `skipRefreshOn401` (el propio retry interno, para cortar loops).
    //  - `skipAuth` (la request no llevaba Authorization, renovar no aplica).
    if (shouldRenewPass(apiError) && !skipRefreshOn401 && !skipAuth) {
      try {
        await useSessionStore.getState().renew();
      } catch (renewError) {
        // `renew` ya dejó la sesión como corresponde: cerrada (401), "sin
        // acceso" (403 `EFFETA_ROLE_UNKNOWN`) o abierta con un reintento
        // agendado. En el último caso el motivo útil es el del refresh
        // (Effetá no responde), no el 401 del pedido.
        throw classifySessionFailure(renewError) === 'transient' ? renewError : apiError;
      }
      // El retry lee el pase nuevo del store en el merge default de headers.
      // No pisamos `options.headers` para evitar duplicar el header
      // (`Authorization` vs `authorization` colisiona y fetch concatena los
      // valores con coma — el back rechaza el header malformed).
      return send<T>(path, { ...options, skipRefreshOn401: true }, providerRetries);
    }

    if (
      apiError.statusCode === 503 &&
      apiError.errorCode === IDENTITY_PROVIDER_UNAVAILABLE &&
      providerRetries < PROVIDER_RETRY_DELAYS_MS.length
    ) {
      await wait(PROVIDER_RETRY_DELAYS_MS[providerRetries], signal);
      return send<T>(path, options, providerRetries + 1);
    }

    throw apiError;
  }

  if (parseResponse === 'none') {
    // 204 No Content u otros casos donde el caller no quiere parsear body.
    return undefined as T;
  }

  if (parseResponse === 'blob') {
    // Descarga de archivos: devolvemos el blob + filename derivado del header.
    // El caller decide el fallback cuando `filename` viene null.
    const blob = await response.blob();
    const filename = parseContentDispositionFilename(
      response.headers.get('Content-Disposition'),
    );
    return { blob, filename } as T;
  }

  // 204 con parseResponse 'json' → no hay body que parsear, devolvemos undefined.
  if (response.status === 204) {
    return undefined as T;
  }

  const json = (await response.json()) as unknown;
  return unwrapEnvelope<T>(json);
}

// El backend envuelve todas las responses 2xx en `{ data, error, meta }`.
// Frontend espera el contenido directo. Detectamos el shape y desempacamos
// — pero solo cuando es realmente envelope, para no romper handlers MSW
// que devuelven raw o backends que no aplican el wrapper.
//
// Caso especial: si `data` es un array (list endpoints), el frontend espera
// el shape paginated `{ data, total, skip, take }`. Reconstruimos ese shape
// leyendo `meta.pagination` si existe, o derivando defaults del array.
function unwrapEnvelope<T>(value: unknown): T {
  if (
    value !== null &&
    typeof value === 'object' &&
    'data' in value &&
    'error' in value &&
    'meta' in value
  ) {
    const envelope = value as { data: unknown; error: unknown; meta: unknown };
    if (envelope.error !== null && envelope.error !== undefined) {
      // 2xx con error definido: contradictorio. Devolvemos raw para que el
      // caller decida con su schema.
      return value as T;
    }

    if (Array.isArray(envelope.data)) {
      // List endpoint: envolvemos en shape paginated que el frontend espera.
      const pagination = extractPagination(envelope.meta, envelope.data.length);
      return {
        data: envelope.data,
        total: pagination.total,
        skip: pagination.skip,
        take: pagination.take,
        ...(pagination.limitClamped !== undefined && {
          limitClamped: pagination.limitClamped,
        }),
        ...(pagination.appliedSlugs !== undefined && {
          appliedSlugs: pagination.appliedSlugs,
        }),
      } as T;
    }

    if (envelope.data === null) {
      // `data: null` con motivo en `meta` (ej: POST /products/:sku/query
      // cuando el área no lee ninguna fuente del SKU — código
      // AREA_WITHOUT_DATA en `meta.pagination.notice`). Devolver solo
      // `envelope.data` perdía el motivo: el caller recibía `null` a secas y
      // no podía distinguir "sin datos, con aviso" de "sin datos". Preservamos
      // `meta` para que el caller lo lea si lo necesita.
      return { data: null, meta: envelope.meta } as T;
    }

    return envelope.data as T;
  }
  return value as T;
}

interface Pagination {
  total: number;
  skip: number;
  take: number;
  // Multi-source extras del backend (GET /products). Opcionales: si el meta
  // del envelope los trae, los propagamos al shape paginated.
  limitClamped?: boolean;
  appliedSlugs?: string[];
}

// Lee paginación del meta del envelope. Hay dos shapes en el backend:
// - skip/take (legacy products, brands, etc).
// - page/limit/totalPages (hub-service, suggestions admin).
// Convertimos page/limit a skip/take para que el frontend tenga un solo
// shape interno paginated().
function extractPagination(meta: unknown, fallbackLength: number): Pagination {
  // Fallback: cuando `meta.pagination` está ausente, asumimos que el
  // endpoint NO paginó y reportamos `total = fallbackLength`. Esto es
  // correcto para endpoints deliberadamente no paginados (ej: /integrators,
  // /admin/integrators). Riesgo silencioso: si un endpoint PAGINADO
  // devolviese la respuesta sin `meta.pagination` por un bug del back, la
  // UI reportaría el tamaño de la página como el total sin señal de trunco.
  // La señal a mirar es `data.length === limit-request-que-hicimos` — se
  // detecta arriba, no acá, porque el limit no viaja al interceptor.
  const defaults: Pagination = { total: fallbackLength, skip: 0, take: fallbackLength };
  if (meta === null || typeof meta !== 'object') return defaults;
  const m = meta as Record<string, unknown>;
  const candidate =
    m.pagination && typeof m.pagination === 'object'
      ? (m.pagination as Record<string, unknown>)
      : m;
  const limit = numberOr(candidate.limit, NaN);
  const page = numberOr(candidate.page, NaN);
  const skipFromPage =
    Number.isFinite(page) && Number.isFinite(limit) ? (page - 1) * limit : NaN;
  const limitClamped =
    typeof candidate.limitClamped === 'boolean' ? candidate.limitClamped : undefined;
  const appliedSlugs = Array.isArray(candidate.appliedSlugs)
    ? (candidate.appliedSlugs as unknown[]).filter(
        (s): s is string => typeof s === 'string',
      )
    : undefined;
  return {
    total: numberOr(candidate.total, defaults.total),
    skip: numberOr(candidate.skip, Number.isFinite(skipFromPage) ? skipFromPage : defaults.skip),
    take: numberOr(candidate.take, Number.isFinite(limit) ? limit : defaults.take),
    ...(limitClamped !== undefined && { limitClamped }),
    ...(appliedSlugs !== undefined && { appliedSlugs }),
  };
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

// Sugar helpers -------------------------------------------------------------
// Pequeñas envolturas para los métodos comunes. Mantienen tipado del genérico
// y permiten omitir `method` en el call site.

type SugarOptions = Omit<HttpOptions, 'method'>;

http.get = <T = unknown>(path: string, options?: SugarOptions): Promise<T> =>
  http<T>(path, { ...options, method: 'GET' });

http.post = <T = unknown>(
  path: string,
  body?: unknown,
  options?: SugarOptions,
): Promise<T> => http<T>(path, { ...options, method: 'POST', body });

http.patch = <T = unknown>(
  path: string,
  body?: unknown,
  options?: SugarOptions,
): Promise<T> => http<T>(path, { ...options, method: 'PATCH', body });

http.put = <T = unknown>(
  path: string,
  body?: unknown,
  options?: SugarOptions,
): Promise<T> => http<T>(path, { ...options, method: 'PUT', body });

http.delete = <T = unknown>(path: string, options?: SugarOptions): Promise<T> =>
  http<T>(path, { ...options, method: 'DELETE' });
